import React, { useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import moment from 'moment';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import LoadingSpinner from 'components/LoadingSpinner';
import Label from 'components/shared/Label/Label';
import useFetchNotes from 'hooks/useFetchNotes';
import SearchInput from 'components/SearchInput';

const useStyles = makeStyles(theme => {
  const { palette, scrollStyles } = theme;

  return {
    root: {
      height: '100%',
      width: '100%'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '40px',
      margin: '1.5rem .5rem 1.5rem .5rem'
    },
    title: {
      color: palette.grey.darker,
      marginRight: '16px',
      fontSize: '1.5rem',
      fontWeight: theme.typography.fontWeightBold
    },
    notesContainer: {
      ...scrollStyles,
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent'
      },
      height: 'calc(100% - 100px)',
      overflow: 'auto',
      padding: '1rem 0'
    },
    note: {
      margin: '.5rem 0'
    },
    noteBody: {
      padding: '.25rem .5rem',
      fontSize: '.825rem',
      color: palette.grey.dark,
      borderLeft: `2px solid ${palette.grey.medium}`,
      borderBottom: `2px solid ${palette.grey.medium}`
    },
    noteHeader: {
      display: 'flex',
      alignItems: 'center',
      color: palette.grey.darker,
      fontSize: '1.3rem',
      fontWeight: theme.typography.fontWeightBold,
      margin: '10px 0'
    },
    noteTitle: {
      marginLeft: '.5rem',
      display: 'inline-block'
    },
    loadingSpinner: {
      padding: 0
    },
    highlight: {
      backgroundColor: palette.highlight
    }
  };
});

const NotesFullscreen = ({ id }) => {
  const [page, setPage] = useState(1);
  const [state, notes, fetchNext, searchText, setSearchText] = useFetchNotes(id);
  const { isLoading, isError, error } = state;
  const classes = useStyles();

  const onSearch = term => {
    setPage(1);
    setSearchText(term);
  };

  let timerId;
  const handleScrolledToBottom = () => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNext(nextPage);
    }, 300);
  };
  const scrollRef = useBottomScrollListener(handleScrolledToBottom, 20, 0);

  const highlightSearchText = html => {
    let result = html;
    if (searchText !== '') {
      result = html.replace(searchText, `<span class=${classes.highlight}>${searchText}</span>`);
    }

    return parse(result, { trim: true });
  };

  const renderNotes = () => {
    return notes.map(note => (
      <div key={note.researchNoteId} className={classes.note}>
        <div className={classes.noteHeader}>
          <Label
            themeColor="primary"
            size="medium"
            text={moment(note.callDate).format('MM/DD/YYYY')}
          />
          <div className={classes.noteTitle}>{`${note.noteKind} by ${note.author}`}</div>
        </div>
        <div className={classes.noteBody}>
          {/* TODO: Why are some notes returned with null htmlContent? */}
          {note.htmlContent && highlightSearchText(note.htmlContent)}
        </div>
      </div>
    ));
  };

  return (
    <div className={classes.root}>
      <Grid className={classes.header} container justify="space-between" alignItems="center">
        <Grid item xs={4}>
          <span className={classes.title}>Notes</span>
        </Grid>
        <Grid item xs={3}>
          <SearchInput style={{ width: '100%' }} onSearch={onSearch} />
        </Grid>
      </Grid>
      {notes.length > 0 && (
        <div className={classes.notesContainer} ref={scrollRef}>
          {renderNotes()}
        </div>
      )}
      {isLoading && <LoadingSpinner classes={{ root: classes.loadingSpinner }} />}
      {isError && (
        <div>
          <span>Oops! Something went wrong.</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

NotesFullscreen.propTypes = {
  id: PropTypes.number.isRequired
};

export default NotesFullscreen;
